import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject } from 'rxjs';
import { BusinessAccountService } from 'src/app/project/postlogin/business-account/business-account.service';
import { OrderFormsService } from 'src/app/project/postlogin/order-management/service/order-forms.service';
import { ApiService } from 'src/app/service/api.service';

@Component({
  selector: 'app-order-transaction-packages',
  templateUrl: './order-transaction-packages.component.html',
  styleUrls: ['./order-transaction-packages.component.scss'],
})
export class OrderTransactionPackagesComponent implements OnInit {
  @Input() productPackages: any;
  @Input() isEditable: any;
  @Input() orderForm: any;
  @Input() productsList;
  @Input() type: string; // 'order' or 'quotation'
  @Output() calculate = new EventEmitter();
  @Output() getProducts = new EventEmitter();
  @Output() generatePoFromPdf = new EventEmitter();
  private searchSubject = new Subject<string>();

  @Input() displayedColumns = [];

  @Input() orderFile: any;

  constructor(
    public orderFormService: OrderFormsService,
    public businessAccountService: BusinessAccountService,
    public toastr: ToastrService,
    public apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.getProducts.emit(value);
    });
  }

  get buyingType() {
    return this.orderForm.get('buyingType');
  }
  get status() {
    return this.orderForm.get('status');
  }

  getMetricCost(productPackage) {
    return (
      productPackage.get('metricCost').value?.attributeValue +
      ' ' +
      productPackage.get('metricCost').value?.userConversionUom
    );
  }

  changeCostByUser(productPackageForm) {
    productPackageForm.get('isCostInputByUser').patchValue(true);
    this.calculateValues();
  }

  changeQuantityInOrder(i) {
    if (!this.productPackages.controls[i].get('productId').value) {
      return;
    }
    if (!this.isEditable) {
      return;
    }
    const quantityControl = this.productPackages.controls[i].get('quantity');
    if (quantityControl.value < 1) {
      quantityControl.patchValue(1);
      return;
    }
    quantityControl.patchValue(quantityControl.value);
    this.calculateValues();
  }

  isQuantityLessThanContainerMqo(i) {
    const productControl = this.productPackages.controls[i];
    const quantityControl = this.productPackages.controls[i].get('quantity');
    if (
      ['CONTAINER_40_FT', 'CONTAINER_20_FT', 'CONTAINER_40_FT_HQ']?.includes(
        this.buyingType?.value
      ) &&
      quantityControl.value < productControl.value.productDetails?.containerMqo
    ) {
      return (
        'Quantity is less than container MQO ' +
        productControl.value.productDetails?.containerMqo
      );
    } else {
      return '';
    }
  }

  updateProductQuantityInOrder(i, quantity) {
    if (!this.productPackages.controls[i].get('productId').value) {
      return;
    }
    if (!this.isEditable) {
      return;
    }
    const quantityControl = this.productPackages.controls[i].get('quantity');

    if (quantityControl.value < 1) {
      quantityControl.patchValue(1);
      return;
    }
    quantityControl.patchValue(quantityControl.value + quantity);
    this.calculateValues();
  }

  addProductPackage() {
    const productPackageForm = this.orderFormService.productPackageForm();
    productPackageForm.get('quantity').patchValue(1);
    this.productPackages.push(productPackageForm);
  }

  deleteProductPackage(i: any) {
    if (this.type == 'quotation' && this.status.value != 'DRAFT') {
      this.toastr.error('You can delete product in Draft status only');
      return;
    }
    this.productPackages.removeAt(i);
    this.calculateValues();
  }

  calculateValues() {
    this.calculate.emit(null);
  }

  onChange(item, productPackage: FormGroup) {
    productPackage.patchValue(item, {
      emitEvent: false,
      onlySelf: true,
    });
    productPackage.get('productDetails').patchValue(item, {
      emitEvent: false,
      onlySelf: true,
    });
    if (
      ['CONTAINER_40_FT', 'CONTAINER_20_FT', 'CONTAINER_40_FT_HQ']?.includes(
        this.buyingType?.value
      )
    ) {
      productPackage.get('quantity').patchValue(item?.containerMqo);
    } else {
      productPackage.get('quantity').patchValue(1);
    }
    productPackage.get('id').patchValue(null);
    productPackage.get('productId').patchValue(item.id);
    productPackage.get('packageId').patchValue(item.skuPackageId);
    this.calculateValues();
  }

  getProductsList(event) {
    this.searchSubject.next(event);
  }

  onChangePercentage(control: any) {
    const value = control.value;
    if (value > 100) {
      this.toastr.warning('Percentage cannot be greater than 100');
      control.patchValue(100);
    } else if (value < 0) {
      this.toastr.warning('Percentage cannot be less than 100');
      control.patchValue(0);
    }
    this.calculateValues();
  }

  getNoteTitle() {
    const item = this.businessAccountService.notesList.find(
      (item) => item.id == this.noteId.value
    );
    return item?.description ?? '';
  }

  onChangeDeliveryCost() {
    this.orderForm.get('deliveryCostInputByUser').patchValue(true);
    this.calculateValues();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault(); // Prevent default behavior
  }
  fileSelected(event: any, type: string, fileInput: HTMLInputElement) {
    event.preventDefault(); // Prevent default behavior
    if (type === 'drop') {
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        this.generatePoFromPdf.emit(files[0]);
        fileInput.value = ''; // Clear the file input after selection
      }
    } else {
      const files = fileInput.files;
      if (files && files.length > 0) {
        this.generatePoFromPdf.emit(files[0]);
        fileInput.value = ''; // Clear the file input after selection
      }
    }
  }

  onPaste(event) {
    const items = event.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind) {
          const pastedFile = item.getAsFile();
          if (pastedFile) {
            this.generatePoFromPdf.emit(pastedFile);
          }
        }
      }
    }
  }

  deleteFile(file, i) {
    const oldmediaurls = this.media_urls.value ?? [];
    this.apiService.deleteFiles(file).then((res: any) => {
      oldmediaurls.splice(i, 1);
      this.media_urls.patchValue(oldmediaurls);
    });
  }
  uploadFiles(files: any, fileinput: HTMLInputElement) {
    this.apiService.uploadFiles(files).then(
      (res: any) => {
        const resp: any = res;
        const media_url_ids: any[] = [];
        resp.data.forEach((element) => {
          media_url_ids.push(element.id);
        });
        const oldmediaurlids = this.media_url_ids.value ?? [];
        const oldmediaurls = this.media_urls.value ?? [];
        const media_url_merge_ids = [...oldmediaurlids, ...media_url_ids];
        const media_urls = [...oldmediaurls, ...resp.data];
        this.media_urls.patchValue(media_urls);
        this.media_url_ids.patchValue(media_url_merge_ids);
        fileinput.value = ''; // Clear the file input after selection
      },
      (err) => {
        fileinput.value = ''; // Clear the file input after selection
      }
    );
  }
  get noteId() {
    return this.orderForm.get('noteTemplate').get('id');
  }

  get media_url_ids() {
    return this.orderForm.get('media_url_ids');
  }

  get media_urls() {
    return this.orderForm.get('media_urls');
  }
}
